import { useState, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Star, Quote } from 'lucide-react';

/**
 * Testimonial Carousel Component
 * Auto-scrolling testimonial cards dengan hover pause
 */
export default function TestimonialCarousel() {
  const scrollContainerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  const testimonials = [
    {
      id: 1,
      name: 'Budi Santoso',
      role: 'Founder, TechStartup.id',
      image: '👨‍💼',
      rating: 5,
      text: 'Arnata sangat membantu kami membangun dashboard monitoring IoT dari nol. Komunikasi responsif, hasil memuaskan, dan tepat waktu. Highly recommended!',
      project: 'IoT Dashboard System'
    },
    {
      id: 2,
      name: 'Sarah Wijaya',
      role: 'Owner, Warung Kopi Sarah',
      image: '👩‍💼',
      rating: 5,
      text: 'Website company profile yang dibuat sangat profesional dan modern. Proses konsultasi jelas, harga terjangkau untuk UMKM seperti kami.',
      project: 'Company Profile Website'
    },
    {
      id: 3,
      name: 'Ahmad Rizki',
      role: 'Mahasiswa Teknik Elektro',
      image: '👨‍🎓',
      rating: 5,
      text: 'Bimbingan MATLAB dan simulasi kontrolnya sangat detail. Saya jadi paham konsep yang sebelumnya bingung. Worth it banget!',
      project: 'MATLAB Control System'
    },
    {
      id: 4,
      name: 'Linda Kusuma',
      role: 'Product Manager, DigitalCo',
      image: '👩‍💻',
      rating: 5,
      text: 'Deployment dan DevOps setup berjalan lancar. Arnata tidak hanya mengerjakan, tapi juga menjelaskan prosesnya. Sangat membantu tim kami!',
      project: 'DevOps & Cloud Setup'
    },
    {
      id: 5,
      name: 'Dimas Pratama',
      role: 'Founder, AgriTech Solutions',
      image: '👨‍🌾',
      rating: 5,
      text: 'Project greenhouse automation dengan ESP32 sukses! Real-time monitoring dan kontrol berjalan stabil. Team sangat patient dalam menjelaskan.',
      project: 'Greenhouse IoT Automation'
    },
    {
      id: 6,
      name: 'Fitri Rahayu',
      role: 'Mahasiswa Teknik Mesin',
      image: '👩‍🔧',
      rating: 5,
      text: 'Bimbingan Fusion 360 untuk final project sangat helpful. Dijelaskan step by step sampai paham. Hasilnya desain saya jadi lebih rapi!',
      project: 'Fusion 360 Training'
    },
    {
      id: 7,
      name: 'Reza Firmansyah',
      role: 'CTO, SmartHome Indonesia',
      image: '👨‍💻',
      rating: 5,
      text: 'Full-stack development untuk dashboard internal kami excellent! Code clean, dokumentasi lengkap, dan mudah di-maintain. Professional work!',
      project: 'Full-Stack Dashboard'
    },
    {
      id: 8,
      name: 'Maya Sari',
      role: 'Owner, Toko Kue Maya',
      image: '👩‍🍳',
      rating: 5,
      text: 'Landing page untuk online shop kami simple tapi elegan. Loading cepat, mobile-friendly, dan yang penting customer mudah order!',
      project: 'E-commerce Landing Page'
    }
  ];

  // Duplicate testimonials for seamless loop (2 sets cukup untuk CSS animation)
  const duplicatedTestimonials = [...testimonials, ...testimonials];

  return (
    <div className="relative w-full overflow-hidden">
      {/* Gradient Overlays - More Smooth & Wider */}
      <div className="absolute left-0 top-0 bottom-0 w-48 md:w-64 bg-gradient-to-r from-slate-900/90 via-slate-900/50 to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-48 md:w-64 bg-gradient-to-l from-slate-900/90 via-slate-900/50 to-transparent z-10 pointer-events-none"></div>

      {/* Scrolling Container with CSS Animation */}
      <div
        ref={scrollContainerRef}
        className={`flex gap-6 py-4 ${isPaused ? '' : 'animate-scroll-smooth'}`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {duplicatedTestimonials.map((testimonial, index) => (
          <Card
            key={`${testimonial.id}-${index}`}
            className="glass-effect border-slate-700 p-6 flex-shrink-0 w-[400px] group hover:border-cyan-500/50 hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300"
            style={{ 
              backfaceVisibility: 'hidden',
              transform: 'translateZ(0)',
              WebkitFontSmoothing: 'antialiased'
            }}
          >
            {/* Quote Icon */}
            <div className="text-cyan-400/30 mb-4">
              <Quote size={32} className="transition-transform duration-300 group-hover:scale-110" />
            </div>

            {/* Testimonial Text */}
            <p className="text-slate-300 text-sm leading-relaxed mb-4 line-clamp-4 transition-colors duration-300 group-hover:text-white">
              "{testimonial.text}"
            </p>

            {/* Project Tag */}
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-cyan-500/10 text-cyan-400 text-xs rounded-full border border-cyan-500/30 transition-all duration-300 group-hover:bg-cyan-500/20">
                {testimonial.project}
              </span>
            </div>

            {/* Rating Stars */}
            <div className="flex gap-1 mb-4">
              {[...Array(testimonial.rating)].map((_, i) => (
                <Star
                  key={i}
                  size={16}
                  className="text-yellow-400 fill-yellow-400 transition-transform duration-200 group-hover:scale-110"
                  style={{ transitionDelay: `${i * 50}ms` }}
                />
              ))}
            </div>

            {/* Author Info */}
            <div className="flex items-center gap-3 pt-4 border-t border-slate-700/50">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400/20 to-blue-600/20 flex items-center justify-center text-2xl transition-transform duration-300 group-hover:scale-110">
                {testimonial.image}
              </div>
              <div>
                <div className="font-semibold text-white transition-colors duration-300 group-hover:text-cyan-400" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {testimonial.name}
                </div>
                <div className="text-slate-400 text-xs">{testimonial.role}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Pause Indicator */}
      {isPaused && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
          <div className="px-4 py-2 bg-slate-800/90 border border-cyan-500/50 rounded-full text-cyan-400 text-xs font-medium animate-pulse">
            Hover untuk berhenti scroll
          </div>
        </div>
      )}
    </div>
  );
}
